<template>
  <div id="app" :class="appShellClass">
    <template v-if="shouldShowHeader">
      <SitterOnboardingNav v-if="onboardingLayout" />
      <GlobalNav v-else-if="isParent" />
      <SitterNav v-else-if="isSitter" />
      <PublicNav v-else />
    </template>
    <EmailVerificationBanner
      v-if="shouldMountEmailVerificationBanner"
      @visibility-change="emailVerificationBannerVisible = $event"
    />
    <ContentfulBanner
      v-if="shouldShowBanner"
      :segment-event-name="'UpgradePricing_PostLogin_Banner'"
      :offset-for-email-verification-banner="emailVerificationBannerVisible"
    />
    <div class="flex-1">
      <router-view />
    </div>
    <PoppinsCondensedFooter v-if="shouldShowFooter" />
  </div>
</template>

<script lang="ts">
import PoppinsCondensedFooter from '@/components/condensed_footer/PoppinsCondensedFooter.vue';
import ContentfulBanner from '@/components/contentful_banner/ContentfulBanner.vue';
import EmailVerificationBanner from '@/components/email_verification_banner/EmailVerificationBanner.vue';
import GlobalNav from '@/components/nav/GlobalNav.vue';
import PublicNav from '@/components/nav/PublicNav.vue';
import SitterNav from '@/components/nav/SitterNav.vue';
import SitterOnboardingNav from '@/components/navigation/SitterOnboardingNav.vue';
import { readMetaBoolean } from '@/lib/page';
import shouldShowContentfulBanner from '@/helpers/bannerDisplayLogic';

export default {
  name: 'App',
  components: {
    ContentfulBanner,
    EmailVerificationBanner,
    GlobalNav,
    PoppinsCondensedFooter,
    PublicNav,
    SitterNav,
    SitterOnboardingNav,
  },
  data() {
    return {
      emailVerificationBannerVisible: false,
    };
  },
  computed: {
    isParent() {
      return this.account?.isParent() ?? false;
    },
    isSitter() {
      return this.account?.isSitter() ?? false;
    },
    shouldShowBanner() {
      // EV banner replaces the value-plans (Contentful) banner until the user verifies.
      if (this.emailVerificationBannerVisible) {
        return false;
      }
      return shouldShowContentfulBanner(this.account, this.$route.path);
    },
    shouldMountEmailVerificationBanner() {
      // Only mount when authenticated nav chrome is available (Parent or Sitter).
      return this.shouldShowHeader && (this.isParent || this.isSitter);
    },
    /**
     * Nav padding stays nav-only (EV banner has its own spacer). Expand --nav-height when
     * the EV banner is visible so fixed/sticky page chrome (e.g. job-search filters) clears it.
     * Extra 3.5rem matches EmailVerificationBanner (leading-10 + py-8) / spacer h-56.
     */
    appShellClass() {
      if (!this.shouldShowHeader) {
        return '[--nav-height:0px]';
      }
      if (this.emailVerificationBannerVisible) {
        return ['pt-48 md-1:pt-80', '[--nav-height:calc(48px+3.5rem)]', 'md-1:[--nav-height:calc(80px+3.5rem)]'].join(' ');
      }
      return 'pt-48 md-1:pt-80 [--nav-height:48px] md-1:[--nav-height:80px]';
    },
    shouldShowFooter() {
      return !this.$route.matched?.some((r) => r.meta?.shouldHideFooter);
    },
    nativeMobileApp() {
      return readMetaBoolean('native_mobile_app');
    },
    shouldShowHeader() {
      const hideFromMeta = this.$route.matched?.some((r) => r.meta?.hideMainHeader);
      return !hideFromMeta && !this.nativeMobileApp;
    },
    onboardingLayout() {
      return this.$route.matched?.some((r) => r.meta?.sitterOnboardingLayout);
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
  },
  watch: {
    shouldMountEmailVerificationBanner(isMounted: boolean) {
      if (!isMounted) {
        this.emailVerificationBannerVisible = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#app {
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh);
}
</style>
